import {
    type VueFinalModal,
    useModal as useDefaultModal,
    useModalSlot,
    type UseModalOptions,
    type ComponentProps,
} from 'vue-final-modal';
import BaseModal from '@components/base/mr-modal/BaseModal.vue';

export interface UseModalResult<R, P extends Component> {
    close: () => Promise<string>;
    open: (attrs?: UseModalOptions<P>['attrs']) => Promise<{
        reason: 'event' | 'model' | 'close';
        args?: [R, ...any[]];
    }>;
}

export function useModal<
    R = any,
    P extends Component = InstanceType<typeof VueFinalModal>['$props'],
>(
    _options: UseModalOptions<P> & {
        modalAttrs?: UseModalOptions<typeof BaseModal>['attrs'];
    },
    modalId?: string
): UseModalResult<R, P> {
    let id = modalId ?? ((_options.component as any)?.__name || '');
    if (id) {
        id = `MrP${id}`;
    }
    const {
        isOpened: isClosableOpen,
        open: openClosable,
        close: closeClosable,
    } = useClosable(id);
    let resolve: (args: {
        reason: 'event' | 'model' | 'close';
        args?: [R, ...any[]];
    }) => void = () => {};
    const { open, close, options, patchOptions } = useDefaultModal({
        ..._options,
        component: BaseModal,
        attrs: _options.modalAttrs,
        slots: {
            default: useModalSlot({
                component: _options.component!,
                attrs: {
                    // id,
                    ..._options.attrs,
                    onClose: (...args: [R, ...any[]]) => {
                        resolve({
                            reason: 'event',
                            args,
                        });
                    },
                } as any,
            }),
        },
    });

    watch(isClosableOpen, value => {
        if (!value) {
            close();
        }
    });
    const asyncOpen = async (attrs?: ComponentProps<P>) => {
        if (attrs) {
            patchOptions({
                slots: {
                    default: useModalSlot({
                        component: _options.component!,
                        attrs: {
                            ..._options.attrs,
                            ...attrs,
                            onClose: (...args: [R, ...any[]]) => {
                                resolve({
                                    reason: 'event',
                                    args,
                                });
                            },
                        },
                    }),
                },
            });
        }
        const promise = new Promise<{
            reason: 'event' | 'model' | 'close';
            args?: [R, ...any[]];
        }>(_resolve => {
            resolve = (args: {
                reason: 'event' | 'model' | 'close';
                args?: [R, ...any[]] | undefined;
            }) => {
                _resolve(args);
                close();
                closeClosable();
            };
        });
        await open();
        openClosable();
        watch(
            () => options.modelValue,
            value => {
                if (!value) {
                    resolve({ reason: 'model' });
                }
            }
        );
        return await promise;
    };
    return {
        close,
        open: asyncOpen,
    };
}
